import React from 'react';
import { Modal, Form, Input, InputNumber, Button, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Template } from './types';

interface TemplateModalProps {
  open: boolean;
  loading: boolean;
  editingTemplate: Template | null;
  form: any; // 接收外部传递的form实例
  onSubmit: () => void;
  onCancel: () => void;
}

const TemplateModal: React.FC<TemplateModalProps> = ({
  open,
  loading,
  editingTemplate,
  form,
  onSubmit,
  onCancel
}) => {
  const handleOk = async () => {
    try {
      await form.validateFields();
      onSubmit();
    } catch (error) {
      console.log('表单校验失败:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={editingTemplate ? "编辑模板" : "新增模板"}
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={loading}
      destroyOnHidden
      width={800}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          examples: [{ input: '', output: '' }]
        }}
      >
        <Form.Item
          name="project_name"
          label="项目名称"
          rules={[{ required: true, message: '请输入项目名称' }]}
        >
          <Input placeholder="请输入项目名称" />
        </Form.Item>
        <Form.Item
          name="template_name"
          label="模板名称"
          rules={[{ required: true, message: '请输入模板名称' }]}
        >
          <Input placeholder="请输入模板名称" />
        </Form.Item>
        <Form.Item
          name="background"
          label="背景描述"
          rules={[{ required: true, message: '请输入背景描述' }]}
        >
          <Input.TextArea rows={3} placeholder="请输入背景描述" />
        </Form.Item>
        <Form.Item
          name="stance"
          label="立场"
          rules={[{ required: true, message: '请输入立场' }]}
        >
          <Input placeholder="请输入立场" />
        </Form.Item>
        <Form.Item
          name="emotion_judgement"
          label="情感判断说明"
          rules={[{ required: true, message: '请输入情感判断说明' }]}
        >
          <Input.TextArea rows={3} placeholder="请输入情感判断说明" />
        </Form.Item>
        <Form.Item
          name="label_levels"
          label="标签级别"
          rules={[{ required: true, message: '请输入标签级别' }]}
        >
          <InputNumber min={1} max={5} style={{ width: '100%' }} placeholder="1-5之间的整数" />
        </Form.Item>
        <Form.Item
          name="label_judgement"
          label="标签判断标准"
          rules={[{ required: true, message: '请输入标签判断标准' }]}
        >
          <Input.TextArea 
            rows={4} 
            placeholder='请输入JSON格式的标签判断标准，例如：{"媒体侧": "媒体或者自媒体账号对自动驾驶行业及网约车品牌等进行的相对客观的报道、分析和资讯整合等内容。", "用户侧": "网约车用户的发文，包含用车体验、行程中的直观感受、服务评价等"}'
          />
        </Form.Item>
        <Form.Item
          name="label_system"
          label="标签体系"
          rules={[{ required: true, message: '请输入标签体系' }]}
        >
          <Input.TextArea 
            rows={6} 
            placeholder='请输入JSON格式的标签体系结构，例如：[{"level1": "用户侧", "definition": "网约车用户的发文，包含用车体验、行程中的直观感受、服务评价等", "level2": [{"name": "AI驾驶能力", "level3": [{"name": "认可和接受意愿", "level4": [{"name": "不信任AI", "definition": "用户表达对AI驾驶的不信任等内容。"}]}]}]}]'
          />
        </Form.Item>
        <Form.Item
          name="examples"
          label="示例"
          rules={[{ required: true, message: '请输入示例' }]}
        >
          <Form.List name="examples">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <div key={field.key} style={{ border: '1px solid #d9d9d9', borderRadius: 4, padding: 12, marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontWeight: 500 }}>示例 {index + 1}</span>
                      {fields.length > 1 && (
                        <Button 
                          type="text" 
                          danger 
                          size="small"
                          onClick={() => remove(field.name)}
                        >
                          删除
                        </Button>
                      )}
                    </div>
                    <Form.Item
                      {...field}
                      name={[field.name, 'input']}
                      label="输入"
                      rules={[{ required: true, message: '请输入示例输入' }]}
                    >
                      <Input.TextArea 
                        rows={3} 
                        placeholder="请输入示例输入文本"
                      />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, 'output']}
                      label="输出"
                      rules={[{ required: true, message: '请输入示例输出' }]}
                    >
                      <Input.TextArea 
                        rows={2} 
                        placeholder='请输入标签内容，例如：用户侧-安全-其它;中性'
                      />
                    </Form.Item>
                  </div>
                ))}
                <div style={{ marginBottom: 8 }}>
                  <Button 
                    type="dashed" 
                    onClick={() => {
                      if (fields.length < 10) {
                        add({ input: '', output: '' });
                      } else {
                        message.warning('最多只能添加10个示例');
                      }
                    }}
                    icon={<PlusOutlined />}
                  >
                    添加示例
                  </Button>
                  <span style={{ marginLeft: 8, color: '#999', fontSize: 12 }}>
                    最少1个，最多10个示例
                  </span>
                </div>
              </>
            )}
          </Form.List>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default TemplateModal;
